import React from 'react';
import {    Box,   Typography, Card,   CardContent,   Button,   Chip,  Table,   TableBody, 
  TableCell,   TableContainer,   TableHead,   TableRow,   Paper,   Grid, 
  Stack} from '@mui/material';
import { CalendarToday as CalendarTodayIcon, CreditCard as CreditCardIcon, AttachMoney as AttachMoneyIcon, People as PeopleIcon } from '@mui/icons-material';
import { firebaseHelper, useDesignFirm } from 'ui';
import { subscriptionURL } from '../libs/firebase';
import { DesignFirmType,SubscriptionData } from 'ts-interfaces';
import { myStyles as styles } from '../libs/util';

export const Billing = ({designFirmID}) => {
    const { firm, subscription, loading, error } = useDesignFirm(firebaseHelper.db, designFirmID, subscriptionURL) as {
        firm: DesignFirmType;
        subscription: SubscriptionData | null;
        loading: boolean;
        error: Error | null;
    };
    const paymentHistory = subscription?.paymentHistory || [];
    const status = subscription?.subscription?.status || 'inactive';

    // Format currency
    const formatCurrency = (amount, currency) => {
        if (amount === undefined || currency === undefined) return 0; 
        return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: currency.toUpperCase() 
        }).format(amount);
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
        });
    };    


  if (firm==null || subscription?.subscription === null) { return <div>Loading...</div>; }
  if ( loading==true || error != null) {
    return (
      <Card sx={styles.card} style={{paddingTop:'100px'}}>
        <CardContent sx={styles.emptyState}>
          <Box sx={styles.iconContainer}>
            <CreditCardIcon fontSize="large" sx={{ color: 'text.disabled' }} />
          </Box>
          
          {error && (
            <Typography variant="h6" component="h3">
              Error: {error?.message} Please let us know if you need help at{' '}
              <Typography
              component="a"
              href="mailto:support@mattoboard.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
              support@mattoboard.com
              </Typography>
            </Typography>
          )}        
          {loading==true && 
            <>
            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
            Loading...
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Please wait while we fetch your billing information.
            </Typography>
            </>          
          }
        </CardContent>
      </Card>
    )
  }
  if (firm && subscription == null) {
    return (
      <Card sx={styles.card} style={{paddingTop:'100px'}}>
        <CardContent sx={styles.emptyState}>
          <Box sx={styles.iconContainer}>
            <CreditCardIcon fontSize="large" sx={{ color: 'text.disabled' }} />
          </Box>
          <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
            No subscription found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You don't have a subscription yet. Please contact support for assistance.
          </Typography>
        </CardContent>
      </Card>
    )
  }
  return (
    <Box sx={styles.container}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Billing & Subscription
      </Typography>
      <Stack>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        Manage your billing information, subscription details, and payment methods.
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb:4 }}>
        Billing Questions? Contact us at <a href="mailto:sabina@mattoboard.com" style={{ color: 'inherit', textDecoration: 'underline' }}>here</a>
      </Typography>

      </Stack>


      <Grid container spacing={3} sx={{ mb: 5 }}>
        {/* Current Plan */}
        <Grid item xs={12} md={4}>
          <Card sx={styles.card}>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Current plan
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                {subscription?.subscription.plan.name}
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Chip 
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  size="small"
                  sx={status === 'active' ? styles.activeChip : styles.inactiveChip}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachMoneyIcon fontSize="small" sx={styles.iconColor} />
                <Typography>
                  {formatCurrency(subscription?.subscription.plan.amount, subscription?.subscription.plan.currency)} / {subscription?.subscription.plan.interval}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PeopleIcon fontSize="small" sx={styles.iconColor} />
                <Typography>{firm.seats} seats</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon fontSize="small" sx={styles.iconColor} />
                <Typography>Renews: { status === 'active' ? formatDate(subscription?.subscription.currentPeriodEnd) : 'N/A'}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Details */}
        <Grid item xs={12} md={4}>
          <Card sx={styles.card} >
            <CardContent style={{paddingTop:'8px'}} >
              <Box sx={{  display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography  variant="h6" component="h2">
                  Billing details
                </Typography>
                <Button  variant="outlined" size='small'  sx={styles.actionButton} onClick={() => window.open(subscription?.billingDetailsLink, '_blank')} >
                  MANAGE
                </Button>
              </Box>
              {firm.domain && (
                <Typography sx={{ mb: 1 }}>
                    Domain: {' '}
                <Typography component="a" href={'https://'+firm.domain} target="_blank" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    https://{firm.domain}
                </Typography>
                </Typography>
              )}
              {paymentHistory.length > 0 ? (
                <Typography>
                  Last payment: {formatDate(paymentHistory[0].paymentDate)}
                </Typography>
              ) : (
                <Typography>No Payment History</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Details */}
        <Grid item xs={12} md={4}>
          <Card sx={styles.card}>
            <CardContent style={{paddingTop:'8px'}} >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Payment details
                </Typography>
                <Button
                  size='small'
                  variant="outlined"
                  sx={styles.actionButton}
                  onClick={() => window.open(subscription?.updatePaymentLink, '_blank')}
                >
                  CHANGE
                </Button>
              </Box>
              {paymentHistory.length > 0  && subscription?.paymentMethod.last4 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon fontSize="small" sx={styles.iconColor} />
                        <Typography>{paymentHistory[0].paymentMethod.toUpperCase()} •••• {subscription?.paymentMethod.last4} </Typography>
                </Box>
              ) : (
                <Typography>No credit card on file</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Receipts */}
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
        Payment Receipts
      </Typography>
      
      {paymentHistory.length > 0 ? (
        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentHistory.map((invoice) => (
                <TableRow key={invoice.invoiceId}>
                  <TableCell>{invoice.invoiceNumber}</TableCell>
                  <TableCell>{formatDate(invoice.paymentDate)}</TableCell>
                  <TableCell>{formatCurrency(invoice.amountPaid, invoice.currency)}</TableCell>
                  <TableCell align="right">
                    <Button
                      color="primary"
                      onClick={() => window.open(invoice.receiptUrl, '_blank')}
                    >
                      VIEW RECEIPT
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card sx={styles.card}>
          <CardContent sx={styles.emptyState}>
            <Box sx={styles.iconContainer}>
              <CreditCardIcon fontSize="large" sx={{ color: 'text.disabled' }} />
            </Box>
            <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
              No receipts found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You don't have any payment receipts yet. Receipts will appear here when payments are processed.
            </Typography>
          </CardContent>
        </Card>
      )}


    </Box>
  );
};
